import { useEffect, useState } from "react";
import { findImageForList, findImageID, findUnpairedImageID, getdateTimeDiff } from "../global/utility/Utils";
import { useDispatch, useSelector } from "react-redux";
import { saveStreamImageMapping, saveUnpairedCameImageMapping, showNotificationBanner } from "../store/actions";
import { notificationTypes } from "../layouts/NotificationConfig";
import { getCamerabyId } from "../model/reportService";
import { getPairedCamSnapShotIds, getUnpairedCamSnapShotIds } from "../model/cameraonboardingService";
import { ImageDownloadMedia } from "../model/mediaService";
import moment from "moment-timezone";

export const StreamType = {
    PAIRED_CAMERA: "PAIRED_CAMERA",
    UNPAIRED_CAMERA: "UNPAIRED_CAMERA"
}

// Module-level set to remember failed snapshot fetches across mounts.
const snapshotFailedSet = new Set();

export const snapshotImage = (Component, streamId, cameraId, streamType, companionId = "", handleChange) => {

    return (props) => {

        const dispatch = useDispatch();
        const {modalOpenObj} = useSelector((state) => state.cameraOnboardingReducer);
        const { activeSession } = useSelector((state) => state.sessionReducer.activeSession);
        const reducerPairedImages = useSelector((state) => state.imagesReducer.streamImages);
        const reducerUnpairedImages = useSelector((state) => state.imagesReducer.unpairedImages);
        const reducerStreamList = useSelector(state => state.deviceReducer.fovList);
        const reducerUnpairedCameraList = useSelector(state => state.deviceReducer.discoveredCamerasList);
        const customData = (streamType === StreamType.PAIRED_CAMERA) ? findImageID(reducerStreamList, streamId) : findUnpairedImageID(reducerUnpairedCameraList, streamId);
        const matchingImageItem = findImageForList((streamType === StreamType.PAIRED_CAMERA) ? reducerPairedImages : reducerUnpairedImages, streamId)

        const [snapshotImage, setSnapshotImage] = useState("");
        const [fetchedImage, setFetchedImage] = useState([]);
        const [timeStamp, setTimeStamp] = useState("");
        const [loading, setLoading] = useState(false);
        const [streamIds, setStreamIds] = useState([]);
        const fetchKey = `${streamType}_${streamId}`;

        // Track streams that previously failed to fetch so we don't repeatedly
        // call the API on mount. Cleared on successful fetch or when
        // `forceRefresh` is used.
        // Use module-level `snapshotFailedSet` (declared above) to track failures.

        useEffect(() => {
            const fovIds = streamIds?.filter((m) => m !== streamId);
            if (fovIds.length === 0) {
                if (snapshotFailedSet.has(fetchKey)) {
                    // If this stream previously failed, ensure we don't show a loader
                    // and clear any stale in-progress marker.
                    console.log("Skipping fetchSnapshot because previous fetch failed for:", fetchKey);
                    if (matchingImageItem?.imageUrl) {
                        setSnapshotImage(matchingImageItem.imageUrl);
                        handleImageUrl(matchingImageItem.imageUrl);
                    }
                } else {
                    fetchSnapshot();
                }
            }
          }, []);

        const fetchSnapshot = async () => {
           // const customData = (streamType === StreamType.PAIRED_CAMERA) ? findImageID(reducerStreamList, streamId) : findUnpairedImageID(reducerUnpairedCameraList, streamId);
            if (matchingImageItem) {
                if ( getdateTimeDiff(Date.now(), matchingImageItem.timeStamp ) > 1440 
                    || new Date(matchingImageItem.timeStamp) == "Invalid Date") {
                        if(matchingImageItem.imageUrl){
                        setSnapshotImage(matchingImageItem.imageUrl);
                        handleImageUrl(matchingImageItem.imageUrl);
                        }else{
                        await getSnapshotFromApi();
                        }
                        
                } else {
                        setSnapshotImage(matchingImageItem.imageUrl);
                        handleImageUrl(matchingImageItem.imageUrl);
                        setTimeStamp(moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"));
                }
            } else 
            if (customData?.imageId) {
                fetchImage(customData?.imageId, customData?.imagetimestamp);
            } else {
                getSnapshotFromApi();
            }
        }

        const getSnapshotFromApi = (forceRefresh = false) => {
            if (streamType === StreamType.PAIRED_CAMERA) {
                handlePairedSnapshot();
            } else {
                if(matchingImageItem && !forceRefresh) {
                    setSnapshotImage(matchingImageItem.imageUrl);
                    handleImageUrl(matchingImageItem.imageUrl);
                    setTimeStamp(moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"));
                }else{
                    handleUnpairedSnapshot(forceRefresh);
                }
            }
        }

        const handlePairedSnapshot = async () => {
            setLoading(true);
            try {
                const cameraDetails = getCamerabyId(cameraId);
                setStreamIds((streamids) => [...streamids, streamId]);
                const fovData = await getPairedCamSnapShotIds( activeSession, cameraDetails?.[0]?.qrcode, streamId );
                if (fovData?.[0]?.imageid) {
                    fetchImage(fovData[0].imageid, fovData[0].imagetimestamp);
                } else {
                    // No image found for this fov — mark as failed so we don't
                    // continually retry on remount.
                    snapshotFailedSet.add(fetchKey);
                    if (customData?.imageId) {
                        fetchImage(customData?.imageId, customData?.imagetimestamp);
                    }
                    setLoading(false);
                }
            } catch (err) {
                if(matchingImageItem) {
                    setSnapshotImage(matchingImageItem.imageUrl);
                    handleImageUrl(matchingImageItem.imageUrl);
                    setTimeStamp(moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"));
                } else if (customData?.imageId) {
                    fetchImage(customData?.imageId, customData?.imagetimestamp);
                }
                // mark as failed so subsequent mounts won't re-trigger the API
                snapshotFailedSet.add(fetchKey);
                setStreamIds((streamids) => streamids?.filter((m) => m !== streamId));
                setLoading(false);
                console.error("Error fetching snapshot:", err);
            } 
          };

          const handleUnpairedSnapshot = (isRefetch = false) => {
            const isModalOpen = Boolean(Object.keys(modalOpenObj || {}).length);
            if(isModalOpen){
                return;
            }
            setLoading(true);
            const cameraDetails = getCamerabyId(cameraId);
            getUnpairedCamSnapShotIds(activeSession, companionId, cameraId, [streamId])
              .then((fovData) => {
                if (fovData && !fovData?.hasOwnProperty("error")) {
                    if (fovData?.[0]?.imageid) {
                        fetchImage(fovData[0].imageid, fovData[0].timestamp);
                    } else {
                        // No image for this unpaired camera stream — mark failed
                        snapshotFailedSet.add(fetchKey);
                        setLoading(false);
                    }
                }
              })
              .catch((err) => {
                if(matchingImageItem) {
                    setSnapshotImage(matchingImageItem.imageUrl);
                    handleImageUrl(matchingImageItem.imageUrl);
                    setTimeStamp(moment(matchingImageItem.timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"));
                } else if (customData?.imageId) {
                    fetchImage(customData?.imageId, customData?.imagetimestamp);
                }
                if (isRefetch) {
                    dispatch(showNotificationBanner({ type: notificationTypes[111], priority: "critical", showNotification: true }));
                }
                // mark failure so we don't loop on remount
                snapshotFailedSet.add(fetchKey);
                setLoading(false);
                console.error("Error fetching unpaired snapshot:", err);
              })
          };
        const handleImageUrl = (url) => {
            if(handleChange) {
                handleChange(url);
            }
        }
        const fetchImage = async (id, timeStamp) => {
            setLoading(true);
            setStreamIds((streamids) => streamids?.filter((m) => m !== streamId));
            try {
                let url = await ImageDownloadMedia(id, activeSession, streamId, "snapshot");
                if (url) {
                    // success — clear any previous failure marker
                    snapshotFailedSet.delete(fetchKey);
                    if (streamType === StreamType.PAIRED_CAMERA && matchingImageItem?.timeStamp !== timeStamp){
                        dispatch(saveStreamImageMapping(streamId, url, timeStamp));
                    } else {
                        if(matchingImageItem?.timeStamp !== timeStamp) {
                            dispatch(saveUnpairedCameImageMapping(streamId, url, timeStamp));
                        }
                    }
                    fetchedImage.push({imageUrl: url, streamId:  streamId, imageId: id});
                    setFetchedImage(fetchedImage);
                    setSnapshotImage(url);
                    handleImageUrl(url);
                    setTimeStamp(moment(timeStamp).tz(moment.tz.guess()).format("M/D/YYYY, h:mm:ss A z"));
                    setLoading(false);
                } else {
                    setLoading(false);
                    console.log("Image download Error", url);
                    // treat as failure so we don't retry endlessly
                    snapshotFailedSet.add(fetchKey);
                }
            } catch (error) {
                // mark failed
                snapshotFailedSet.add(fetchKey);
                setLoading(false);
                console.error("Error downloading image:", error);
            }
          };

        const refetchImage = () => {
            getSnapshotFromApi(true);
        }

        return <Component {...props} loading = {loading} snapshotImage = {snapshotImage} timeStamp = {timeStamp} refetchImage = {refetchImage} videoSource={snapshotImage}/>
    }
}